import { z } from 'zod';

/**
 * Wave 23 — Unified Activity Feed.
 *
 * Per-project AuditDrawer (Wave 11.4) answered "what happened to this
 * record?". Wave 23 answers "what happened across my portfolio today?"
 * by fanning the same event-store read out to every project the caller
 * can see. Scope rules mirror Carteira/Alertas/Approvals:
 *   - admin → entire tenant
 *   - everyone else → INNER JOIN project_members
 *
 * Events are surfaced raw (no derivations), but grouped by day on the
 * server so the client can render a simple `dateKey → items[]` list
 * without re-bucketing. `actorName` + `projectName` are joined in so the
 * UI never needs a secondary lookup.
 *
 * Category is a coarse classifier (`rdo` / `purchase` / `change_order`
 * / `measurement` / `docs` / `admin` / `other`) derived from
 * `eventType`'s dotted prefix. It drives the filter-chip bar on the
 * page. We deliberately keep the canonical `eventType` string on the
 * wire — the UI can render the full code as a tiny mono label for
 * anyone debugging.
 */

export const activityCategorySchema = z.enum([
  'rdo',
  'purchase',
  'change_order',
  'measurement',
  'docs',
  'admin',
  'other',
]);
export type ActivityCategory = z.infer<typeof activityCategorySchema>;

export const activityItemSchema = z.object({
  /** events.id (ULID). */
  id: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  /** Coarse bucket used by the filter-chip bar. */
  category: activityCategorySchema,
  /** Raw canonical event code, e.g. `rdo.signed`, `po.issued`. */
  eventType: z.string(),
  /** Entity the event belongs to — drives the deep link. */
  entityType: z.string(),
  entityId: z.string(),
  /** Display name of the actor if we can resolve it; null for system events. */
  actorName: z.string().nullable(),
  /** ISO timestamp of when the event happened (UTC). */
  occurredAt: z.string(),
  /** Deep-link into the owning project so a click lands on the right page. */
  href: z.string(),
});
export type ActivityItem = z.infer<typeof activityItemSchema>;

export const activityDayGroupSchema = z.object({
  /** ISO date (YYYY-MM-DD) in UTC — the key the client groups by. */
  dateKey: z.string(),
  items: z.array(activityItemSchema),
});
export type ActivityDayGroup = z.infer<typeof activityDayGroupSchema>;

export const activityFeedCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  byCategory: z.object({
    rdo: z.number().int().nonnegative(),
    purchase: z.number().int().nonnegative(),
    change_order: z.number().int().nonnegative(),
    measurement: z.number().int().nonnegative(),
    docs: z.number().int().nonnegative(),
    admin: z.number().int().nonnegative(),
    other: z.number().int().nonnegative(),
  }),
});
export type ActivityFeedCounts = z.infer<typeof activityFeedCountsSchema>;

export const activityProjectCountSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  count: z.number().int().nonnegative(),
});
export type ActivityProjectCount = z.infer<typeof activityProjectCountSchema>;

export const activityFeedSchema = z.object({
  counts: activityFeedCountsSchema,
  /** Per-project tally of events in the window, ordered count desc → name. */
  byProject: z.array(activityProjectCountSchema),
  /** Day groups ordered newest-first; items inside each group newest-first too. */
  days: z.array(activityDayGroupSchema),
  /** How many days back the caller requested (and we actually returned). */
  windowDays: z.number().int().positive(),
  generatedAt: z.string(),
});
export type ActivityFeed = z.infer<typeof activityFeedSchema>;
